/**
 * Phase2RailScrollArrows.tsx
 * The always-visible floating ◀/▶ week-scroll arrows for the shared Phase-2 rail.
 * A zero-height sticky overlay keeps them centred in the viewport on the rail's
 * left/right edges, so you can page weeks without hunting for the (hidden) scrollbar.
 * Extracted from Phase2Panel. Shown only when there's room to scroll that direction.
 */

import React from 'react';
import { Box, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { alpha, useTheme } from '@mui/material/styles';

export interface Phase2RailScrollArrowsProps {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  /** Page the rail one week left (-1) or right (+1). */
  onScroll: (dir: -1 | 1) => void;
}

export const Phase2RailScrollArrows: React.FC<Phase2RailScrollArrowsProps> = ({
  canScrollLeft,
  canScrollRight,
  onScroll,
}) => {
  const theme = useTheme();
  const btnSx = {
    position: 'absolute' as const,
    transform: 'translateY(-50%)',
    pointerEvents: 'auto' as const,
    backgroundColor: alpha(theme.palette.background.paper, 0.92),
    color: theme.schedule.machineTime,
    border: `1px solid ${theme.border.color}`,
    boxShadow: 2,
    '&:hover': { backgroundColor: theme.palette.background.paper },
  };
  return (
    <Box
      aria-hidden="true"
      sx={{ position: 'sticky', bottom: '50%', height: 0, zIndex: 6, pointerEvents: 'none' }}
    >
      {canScrollLeft && (
        <IconButton onClick={() => onScroll(-1)} aria-label="Scroll to earlier weeks" sx={{ ...btnSx, left: 4 }}>
          <ChevronLeftIcon />
        </IconButton>
      )}
      {canScrollRight && (
        <IconButton onClick={() => onScroll(1)} aria-label="Scroll to later weeks" sx={{ ...btnSx, right: 4 }}>
          <ChevronRightIcon />
        </IconButton>
      )}
    </Box>
  );
};
